import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBuildingMonument,
  IconBuildingStore,
  IconChevronRight,
  IconColumns,
  IconHome,
  IconLock,
  IconLockOpen,
  IconPlus,
  IconPencil,
  IconPhoto,
  IconSearch,
  IconTrash,
  IconHeart,
  IconUsers,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import type {
  RoomJoinMode,
  RoomLayoutOption,
  RoomThemeOption,
  RoomType,
} from "../../shared/types";
import { ROOM_TYPE_CONFIG } from "../../shared/types";
import type { FriendPresence, RoomWithAccess, Session } from "../lib/api";
import { fetchLayouts, fetchRoomThemes } from "../lib/api";
import type { Icon } from "@tabler/icons-react";
import { LayoutPreviewCanvas } from "../components/LayoutPreviewCanvas";

const ROOM_ICONS: Record<
  RoomType,
  { icon: Icon; color: string; glow: string }
> = {
  home: { icon: IconHome, color: "#2dd4bf", glow: "rgba(45, 212, 191, 0.15)" },
  "town-hall": {
    icon: IconColumns,
    color: "#a78bfa",
    glow: "rgba(167, 139, 250, 0.15)",
  },
  shop: {
    icon: IconBuildingStore,
    color: "#fb923c",
    glow: "rgba(251, 146, 60, 0.15)",
  },
  gallery: {
    icon: IconPhoto,
    color: "#60a5fa",
    glow: "rgba(96, 165, 250, 0.15)",
  },
  museum: {
    icon: IconBuildingMonument,
    color: "#f472b6",
    glow: "rgba(244, 114, 182, 0.15)",
  },
};

function joinModeLabel(mode: RoomJoinMode): string {
  switch (mode) {
    case "open":
      return "Open";
    case "members":
      return "Members";
    case "password":
      return "Password";
    case "token":
      return "Token";
    default:
      return "Members";
  }
}

interface Props {
  session: Session;
  rooms: RoomWithAccess[];
  roomsLoading: boolean;
  searchQuery: string;
  onSearchRooms: (query: string) => void;
  onJoinRoom: (roomId: string) => void | Promise<void>;
  onCreateRoom: (
    name: string,
    isPrivate: boolean,
    layoutId?: string,
    wallThemeId?: string,
    floorThemeId?: string,
    options?: {
      joinMode?: RoomJoinMode;
      joinPassword?: string;
      tokenGateContract?: string;
      allowKnock?: boolean;
    }
  ) => Promise<RoomWithAccess>;
  onDeleteRoom: (roomId: string) => Promise<void>;
  onRenameRoom: (roomId: string, name: string) => Promise<void>;
  onToggleRoomPrivacy: (roomId: string, isPrivate: boolean) => Promise<void>;
  onKnockRoom: (roomId: string) => Promise<void>;
  friendsOnline: FriendPresence[];
}

/** Editable room name — inline text field for owners */
function EditableRoomName({
  room,
  isOwner,
  onRename,
}: {
  room: RoomWithAccess;
  isOwner: boolean;
  onRename: (roomId: string, name: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(room.name);
  const [error, setError] = useState("");

  if (!isOwner || !editing) {
    return (
      <Group gap={4} align="center" style={{ minWidth: 0 }}>
        <Text fw={600} size="sm" truncate style={{ flex: 1 }} c="dark">
          {room.name}
        </Text>
        {isOwner && (
          <ActionIcon
            variant="subtle"
            size="xs"
            color="gray"
            onClick={(e) => {
              e.stopPropagation();
              setDraft(room.name);
              setError("");
              setEditing(true);
            }}
          >
            <IconPencil size={11} />
          </ActionIcon>
        )}
      </Group>
    );
  }

  const save = async () => {
    const trimmed = draft.trim().slice(0, 48);
    if (!trimmed) {
      setEditing(false);
      return;
    }
    if (trimmed === room.name) {
      setEditing(false);
      return;
    }
    try {
      await onRename(room.id, trimmed);
      setEditing(false);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename");
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <TextInput
        size="xs"
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") {
            setDraft(room.name);
            setEditing(false);
          }
        }}
        maxLength={48}
        autoFocus
        error={error || undefined}
      />
    </Box>
  );
}

function LocationCard({
  room,
  isHome,
  isOwner,
  onJoin,
  onRename,
  canDelete,
  onDelete,
}: {
  room: RoomWithAccess;
  isHome: boolean;
  isOwner: boolean;
  onJoin: () => void;
  onRename: (roomId: string, name: string) => Promise<void>;
  canDelete?: boolean;
  onDelete?: (roomId: string) => Promise<void>;
}) {
  const config = ROOM_TYPE_CONFIG[room.roomType] ?? ROOM_TYPE_CONFIG.home;
  const iconCfg = ROOM_ICONS[room.roomType] ?? ROOM_ICONS.home;
  const RoomIcon = iconCfg.icon;

  return (
    <Box
      style={{
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.2s ease",
        borderRadius: 14,
        padding: 1,
        background: isHome
          ? "linear-gradient(135deg, rgba(45,212,191,0.3), rgba(45,212,191,0.08))"
          : "rgba(0, 0, 0, 0.06)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = `0 12px 32px ${iconCfg.glow}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
      onClick={onJoin}
    >
      <Box
        p="md"
        style={{
          borderRadius: 13,
          background: "#ffffff",
        }}
      >
        <Group gap="md" align="flex-start">
          {/* Icon */}
          <Box
            style={{
              width: 44,
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: iconCfg.glow,
              borderRadius: 10,
              flexShrink: 0,
            }}
          >
            <RoomIcon size={22} stroke={1.5} color={iconCfg.color} />
          </Box>

          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group justify="space-between" align="center" mb={2}>
              <EditableRoomName
                room={room}
                isOwner={isOwner}
                onRename={onRename}
              />
              <Group gap={6} wrap="nowrap" align="center">
                {isHome && (
                  <Badge
                    size="xs"
                    variant="light"
                    color="teal"
                    style={{ flexShrink: 0 }}
                  >
                    Home
                  </Badge>
                )}
                <Badge
                  size="xs"
                  variant="light"
                  color={room.joinMode === "open" ? "green" : "orange"}
                  style={{ flexShrink: 0 }}
                >
                  {joinModeLabel(room.joinMode)}
                </Badge>
                {canDelete && onDelete && (
                  <Tooltip label="Delete room">
                    <ActionIcon
                      variant="subtle"
                      size="xs"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        void onDelete(room.id);
                      }}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
            <Text size="xs" c="gray.6" mb={8}>
              {config.description}
            </Text>
            <Group justify="space-between" align="center">
              <Group gap={6}>
                {room.onlineCount > 0 && (
                  <Group gap={4}>
                    <Box
                      w={6}
                      h={6}
                      style={{
                        borderRadius: "50%",
                        background: "#22c55e",
                        boxShadow: "0 0 6px rgba(34,197,94,0.5)",
                      }}
                    />
                    <Text size="xs" c="green.7" fw={500}>
                      {room.onlineCount}
                    </Text>
                  </Group>
                )}
                <Text size="xs" c="gray.6">
                  /{room.capacity}
                </Text>
              </Group>
              <IconChevronRight
                size={16}
                color="rgba(0,0,0,0.2)"
                style={{ flexShrink: 0 }}
              />
            </Group>
          </Box>
        </Group>
      </Box>
    </Box>
  );
}

/** Room browser list row */
function RoomRow({
  room,
  isOwner,
  onJoin,
  onKnock,
  onRename,
  onTogglePrivacy,
  canDelete,
  onDelete,
}: {
  room: RoomWithAccess;
  isOwner: boolean;
  onJoin: () => void;
  onKnock: () => void;
  onRename: (roomId: string, name: string) => Promise<void>;
  onTogglePrivacy: (roomId: string, isPrivate: boolean) => Promise<void>;
  canDelete?: boolean;
  onDelete?: (roomId: string) => Promise<void>;
}) {
  const iconCfg = ROOM_ICONS[room.roomType] ?? ROOM_ICONS.home;
  const RoomIcon = iconCfg.icon;

  return (
    <Group
      gap="sm"
      px="sm"
      py={8}
      style={{
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        cursor: room.canJoin ? "pointer" : "default",
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(0, 0, 0, 0.02)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "";
      }}
      onClick={room.canJoin ? onJoin : undefined}
    >
      <RoomIcon size={16} stroke={1.5} color={iconCfg.color} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <EditableRoomName room={room} isOwner={isOwner} onRename={onRename} />
      </Box>
      <Text size="xs" c="gray.5" ff="monospace" style={{ flexShrink: 0 }}>
        {room.ownerAddress.slice(0, 6)}..
      </Text>
      <Badge
        size="xs"
        variant="light"
        color={room.joinMode === "open" ? "green" : "orange"}
      >
        {joinModeLabel(room.joinMode)}
      </Badge>
      {isOwner && (
        <Tooltip label={room.isPrivate ? "Make public" : "Make private"}>
          <ActionIcon
            variant="subtle"
            size="xs"
            color={room.isPrivate ? "orange" : "gray"}
            onClick={(e) => {
              e.stopPropagation();
              void onTogglePrivacy(room.id, !room.isPrivate);
            }}
          >
            {room.isPrivate ? (
              <IconLock size={14} />
            ) : (
              <IconLockOpen size={14} />
            )}
          </ActionIcon>
        </Tooltip>
      )}
      {canDelete && onDelete && (
        <Tooltip label="Delete room">
          <ActionIcon
            variant="subtle"
            size="xs"
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              void onDelete(room.id);
            }}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Tooltip>
      )}
      <Group gap={4} style={{ flexShrink: 0 }}>
        {room.onlineCount > 0 && (
          <Box
            w={5}
            h={5}
            style={{ borderRadius: "50%", background: "#22c55e" }}
          />
        )}
        <Text size="xs" c="gray.6" fw={500}>
          {room.onlineCount}/{room.capacity}
        </Text>
      </Group>
      {room.canJoin ? (
        <Button
          variant="subtle"
          size="compact-xs"
          color="orange"
          onClick={(e) => {
            e.stopPropagation();
            onJoin();
          }}
          style={{ flexShrink: 0 }}
        >
          Enter
        </Button>
      ) : !room.canJoin && room.allowKnock ? (
        <Button
          variant="subtle"
          size="compact-xs"
          color="teal"
          onClick={(e) => {
            e.stopPropagation();
            onKnock();
          }}
          style={{ flexShrink: 0 }}
        >
          Knock
        </Button>
      ) : (
        <Button
          variant="subtle"
          size="compact-xs"
          color="orange"
          disabled
          style={{ flexShrink: 0 }}
        >
          {room.requiresLayoutUnlock && !room.hasLayoutAccess
            ? "Locked"
            : "Private"}
        </Button>
      )}
    </Group>
  );
}

export function LobbyScreen({
  session,
  rooms,
  roomsLoading,
  searchQuery,
  onSearchRooms,
  onJoinRoom,
  onCreateRoom,
  onDeleteRoom,
  onRenameRoom,
  onToggleRoomPrivacy,
  onKnockRoom,
  friendsOnline,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("My Drobbit Room");
  const [createPrivate, setCreatePrivate] = useState(false);
  const [createJoinMode, setCreateJoinMode] = useState<RoomJoinMode>("open");
  const [createJoinPassword, setCreateJoinPassword] = useState("");
  const [createTokenGateContract, setCreateTokenGateContract] = useState("");
  const [createTokenGateTokenId, setCreateTokenGateTokenId] = useState("");
  const [createAllowKnock, setCreateAllowKnock] = useState(true);
  const [layoutChoices, setLayoutChoices] = useState<RoomLayoutOption[]>([]);
  const [themeChoices, setThemeChoices] = useState<RoomThemeOption[]>([]);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null);
  const [selectedWallThemeId, setSelectedWallThemeId] = useState<string | null>(
    null
  );
  const [selectedFloorThemeId, setSelectedFloorThemeId] = useState<
    string | null
  >(null);
  const worldRooms = rooms.filter((r) => r.roomType !== "home" && r.canJoin);
  const myRooms = rooms.filter(
    (r) => r.roomType === "home" && r.ownerAddress === session.address
  );
  const otherRooms = rooms.filter(
    (r) => r.roomType === "home" && r.ownerAddress !== session.address
  );
  const isSearching = searchQuery.trim().length > 0;
  const canCreateRoom = session.isAdmin || myRooms.length < 3;

  const selectedLayout = useMemo(
    () =>
      layoutChoices.find((layout) => layout.id === selectedLayoutId) ?? null,
    [layoutChoices, selectedLayoutId]
  );
  const selectedWallTheme = useMemo(
    () =>
      themeChoices.find((theme) => theme.id === selectedWallThemeId) ?? null,
    [themeChoices, selectedWallThemeId]
  );
  const selectedFloorTheme = useMemo(
    () =>
      themeChoices.find((theme) => theme.id === selectedFloorThemeId) ?? null,
    [themeChoices, selectedFloorThemeId]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [layouts, themes] = await Promise.all([
          fetchLayouts(session),
          fetchRoomThemes(session),
        ]);
        if (cancelled) return;
        const availableLayouts = layouts.filter(
          (layout) => layout.state === "published" && layout.unlocked
        );
        const availableThemes = themes.filter(
          (theme) => theme.state === "published" && theme.unlocked
        );
        setLayoutChoices(availableLayouts);
        setThemeChoices(availableThemes);
        setSelectedLayoutId((prev) => prev ?? availableLayouts[0]?.id ?? null);
        setSelectedWallThemeId(
          (prev) => prev ?? availableThemes[0]?.id ?? null
        );
        setSelectedFloorThemeId(
          (prev) => prev ?? availableThemes[0]?.id ?? null
        );
      } catch {
        if (cancelled) return;
        setLayoutChoices([]);
        setThemeChoices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const handleDeleteRoom = async (roomId: string) => {
    const target = rooms.find((room) => room.id === roomId);
    if (!target) return;
    const confirmed = window.confirm(
      `Delete "${target.name}"? This permanently removes all objects, paintings, and room settings.`
    );
    if (!confirmed) return;
    try {
      await onDeleteRoom(roomId);
      notifications.show({
        title: "Room deleted",
        message: `${target.name} was removed.`,
        color: "orange",
      });
    } catch (error) {
      notifications.show({
        title: "Could not delete room",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    }
  };

  const handleCreateRoom = async () => {
    const name = createName.trim().slice(0, 48);
    if (!name) {
      notifications.show({
        title: "Invalid room name",
        message: "Please enter a room name.",
        color: "red",
      });
      return;
    }
    if (!canCreateRoom) {
      notifications.show({
        title: "Room limit reached",
        message: "Normal users can create up to 3 rooms.",
        color: "orange",
      });
      return;
    }
    if (!selectedLayout || !selectedWallTheme || !selectedFloorTheme) {
      notifications.show({
        title: "Missing selection",
        message: "Pick a layout, wallpaper, and floor theme.",
        color: "red",
      });
      return;
    }
    const joinMode = createJoinMode;
    const isPrivate = joinMode !== "open";
    const password = createJoinPassword.trim();
    if (
      joinMode === "password" &&
      (password.length < 3 || password.length > 64)
    ) {
      notifications.show({
        title: "Password required",
        message: "Use a password between 3 and 64 characters.",
        color: "red",
      });
      return;
    }
    const tokenGateContractRaw = createTokenGateContract.trim();
    const tokenGateTokenId = createTokenGateTokenId.trim();
    const tokenGateContract = tokenGateTokenId
      ? `${tokenGateContractRaw}:${tokenGateTokenId}`
      : tokenGateContractRaw;
    if (joinMode === "token") {
      if (!tokenGateContractRaw || !tokenGateContractRaw.startsWith("KT1")) {
        notifications.show({
          title: "Invalid token contract",
          message: "Token-gated rooms require a valid KT1 contract.",
          color: "red",
        });
        return;
      }
    }
    setCreating(true);
    try {
      const room = await onCreateRoom(
        name,
        isPrivate,
        selectedLayout.id,
        selectedWallTheme.id,
        selectedFloorTheme.id,
        {
          joinMode,
          joinPassword: joinMode === "password" ? password : undefined,
          tokenGateContract:
            joinMode === "token" ? tokenGateContract : undefined,
          allowKnock: joinMode === "open" ? false : createAllowKnock,
        }
      );
      notifications.show({
        title: "Room created",
        message: room.name,
        color: "teal",
      });
      setCreateJoinPassword("");
      setCreateOpen(false);
      void onJoinRoom(room.id);
    } catch (error) {
      notifications.show({
        title: "Could not create room",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Container size="md" py="xl">
      {/* World locations */}
      {!isSearching && (
        <Stack gap="md" mb="xl">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {worldRooms.map((room) => (
              <LocationCard
                key={room.id}
                room={room}
                isHome={false}
                isOwner={room.ownerAddress === session.address}
                onJoin={() => onJoinRoom(room.id)}
                onRename={onRenameRoom}
              />
            ))}
          </SimpleGrid>
        </Stack>
      )}

      {/* Room Browser */}
      <Stack gap="sm">
        <Group gap="sm" align="center" justify="space-between">
          <Group gap="sm" align="center">
            <IconUsers size={16} color="rgba(0,0,0,0.3)" />
            <Text size="xs" c="gray.6" fw={600} tt="uppercase" lts="0.08em">
              Player Homes
            </Text>
          </Group>
          <Group gap="xs" align="center">
            <Badge
              size="xs"
              color={canCreateRoom ? "teal" : "orange"}
              variant="light"
            >
              {session.isAdmin
                ? `${myRooms.length} owned`
                : `${myRooms.length}/3 owned`}
            </Badge>
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setCreateOpen(true)}
              disabled={!canCreateRoom && !session.isAdmin}
              loading={roomsLoading}
            >
              Create Room
            </Button>
          </Group>
        </Group>

        <TextInput
          placeholder="Search rooms or addresses..."
          value={searchQuery}
          onChange={(e) => onSearchRooms(e.currentTarget.value)}
          size="sm"
          leftSection={<IconSearch size={15} />}
        />

        <Stack gap="sm">
          <Stack gap={6}>
            <Text size="xs" c="gray.6" fw={600} tt="uppercase" lts="0.08em">
              My Rooms
            </Text>
            <Box
              style={{
                background: "#ffffff",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              {myRooms.map((room) => (
                <RoomRow
                  key={room.id}
                  room={room}
                  isOwner
                  onJoin={() => onJoinRoom(room.id)}
                  onKnock={() => void onKnockRoom(room.id)}
                  onRename={onRenameRoom}
                  onTogglePrivacy={onToggleRoomPrivacy}
                  canDelete
                  onDelete={handleDeleteRoom}
                />
              ))}
              {myRooms.length === 0 && (
                <Box p="md" style={{ textAlign: "center" }}>
                  <Text size="sm" c="gray.5">
                    {isSearching
                      ? "No owned rooms match your search."
                      : "You have no rooms yet."}
                  </Text>
                </Box>
              )}
            </Box>
          </Stack>

          {friendsOnline.length > 0 && (
            <Stack gap={6}>
              <Group gap={6}>
                <IconHeart size={13} color="#ff922b" />
                <Text size="xs" c="gray.6" fw={600} tt="uppercase" lts="0.08em">
                  Friends Online
                </Text>
              </Group>
              <Box
                style={{
                  background: "#ffffff",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                {friendsOnline.map((f) => (
                  <Box
                    key={f.address}
                    p="xs"
                    px="md"
                    onClick={() => onJoinRoom(f.roomId)}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
                      transition: "background 0.15s",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "rgba(0,0,0,0.02)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "";
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={600} c="dark" truncate>
                          {f.displayName || `${f.address.slice(0, 8)}...`}
                        </Text>
                        <Text size="xs" c="gray.5" truncate>
                          {f.roomOwnerName
                            ? `in ${f.roomOwnerName}'s room`
                            : `in ${f.roomName}`}
                        </Text>
                      </Box>
                      <Button
                        size="compact-xs"
                        variant="light"
                        color="orange"
                        onClick={(e) => {
                          e.stopPropagation();
                          onJoinRoom(f.roomId);
                        }}
                      >
                        Visit
                      </Button>
                    </Group>
                  </Box>
                ))}
              </Box>
            </Stack>
          )}

          <Stack gap={6}>
            <Text size="xs" c="gray.6" fw={600} tt="uppercase" lts="0.08em">
              All Rooms
            </Text>
            <Box
              style={{
                background: "#ffffff",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              {otherRooms.map((room) => (
                <RoomRow
                  key={room.id}
                  room={room}
                  isOwner={false}
                  onJoin={() => onJoinRoom(room.id)}
                  onKnock={() => void onKnockRoom(room.id)}
                  onRename={onRenameRoom}
                  onTogglePrivacy={onToggleRoomPrivacy}
                />
              ))}
              {otherRooms.length === 0 && (
                <Box p="md" style={{ textAlign: "center" }}>
                  <Text size="sm" c="gray.5">
                    {isSearching
                      ? "No other rooms match your search."
                      : "No other rooms available."}
                  </Text>
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
      </Stack>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Room"
        size="lg"
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Room Name"
            value={createName}
            onChange={(event) => setCreateName(event.currentTarget.value)}
            maxLength={48}
          />
          <Switch
            label="Private Room"
            checked={createPrivate}
            onChange={(event) => {
              const checked = event.currentTarget.checked;
              setCreatePrivate(checked);
              if (!checked && createJoinMode === "members") {
                setCreateJoinMode("open");
              } else if (checked && createJoinMode === "open") {
                setCreateJoinMode("members");
              }
            }}
          />
          <Select
            label="Join Access"
            value={createJoinMode}
            onChange={(value) => {
              const nextMode = (value as RoomJoinMode | null) ?? "open";
              setCreateJoinMode(nextMode);
              if (nextMode !== "open") {
                setCreatePrivate(true);
              } else {
                setCreatePrivate(false);
              }
            }}
            data={[
              { value: "open", label: "Open (anyone can join)" },
              { value: "members", label: "Owner approval only" },
              { value: "password", label: "Password-gated" },
              { value: "token", label: "Token-gated (FA2)" },
            ]}
          />
          {createJoinMode === "password" && (
            <TextInput
              label="Room Password"
              placeholder="3-64 characters"
              value={createJoinPassword}
              onChange={(event) =>
                setCreateJoinPassword(event.currentTarget.value)
              }
            />
          )}
          {createJoinMode === "token" && (
            <>
              <TextInput
                label="FA2 Contract Address"
                placeholder="KT1..."
                value={createTokenGateContract}
                onChange={(event) =>
                  setCreateTokenGateContract(event.currentTarget.value)
                }
                styles={{
                  input: { fontFamily: "monospace" },
                }}
              />
              <TextInput
                label="Token ID (optional)"
                description="Leave empty to allow any token from this contract"
                placeholder="e.g. 42"
                value={createTokenGateTokenId}
                onChange={(event) =>
                  setCreateTokenGateTokenId(event.currentTarget.value)
                }
                styles={{
                  input: { fontFamily: "monospace" },
                }}
              />
            </>
          )}
          {createJoinMode !== "open" && (
            <Switch
              label="Allow Knock Requests"
              checked={createAllowKnock}
              onChange={(event) =>
                setCreateAllowKnock(event.currentTarget.checked)
              }
            />
          )}

          <Divider my={4} />

          <Text size="sm" fw={600}>
            Layout
          </Text>
          <Box
            style={{
              maxHeight: 230,
              overflowY: "auto",
              display: "grid",
              gap: 10,
              gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
            }}
          >
            {layoutChoices.map((layout) => (
              <Card
                key={layout.id}
                p="xs"
                radius="md"
                style={{
                  cursor: "pointer",
                  border:
                    selectedLayoutId === layout.id
                      ? "1px solid rgba(45, 150, 255, 0.6)"
                      : "1px solid rgba(0,0,0,0.08)",
                  background:
                    selectedLayoutId === layout.id
                      ? "rgba(45, 150, 255, 0.06)"
                      : "#fafbfc",
                }}
                onClick={() => setSelectedLayoutId(layout.id)}
              >
                <Stack gap={8}>
                  <LayoutPreviewCanvas
                    layout={layout}
                    height={100}
                    zoomMultiplier={1.25}
                  />
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Text size="xs" fw={600} c="dark" lineClamp={1}>
                      {layout.name}
                    </Text>
                    <Badge size="xs" variant="light" color="teal">
                      {layout.maxUsers}
                    </Badge>
                  </Group>
                </Stack>
              </Card>
            ))}
          </Box>

          <Text size="sm" fw={600}>
            Wallpaper
          </Text>
          <Box
            style={{
              maxHeight: 145,
              overflowY: "auto",
              display: "grid",
              gap: 10,
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            }}
          >
            {themeChoices.map((theme) => (
              <Card
                key={`wall-${theme.id}`}
                p="xs"
                radius="md"
                style={{
                  cursor: "pointer",
                  border:
                    selectedWallThemeId === theme.id
                      ? "1px solid rgba(45, 150, 255, 0.6)"
                      : "1px solid rgba(0,0,0,0.08)",
                  background:
                    selectedWallThemeId === theme.id
                      ? "rgba(45, 150, 255, 0.06)"
                      : "#fafbfc",
                }}
                onClick={() => setSelectedWallThemeId(theme.id)}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Text size="xs" fw={600} c="dark" lineClamp={1}>
                    {theme.name}
                  </Text>
                  <Box
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: theme.wall,
                      flexShrink: 0,
                    }}
                  />
                </Group>
              </Card>
            ))}
          </Box>

          <Text size="sm" fw={600}>
            Floor
          </Text>
          <Box
            style={{
              maxHeight: 155,
              overflowY: "auto",
              display: "grid",
              gap: 10,
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            }}
          >
            {themeChoices.map((theme) => (
              <Card
                key={`floor-${theme.id}`}
                p="xs"
                radius="md"
                style={{
                  cursor: "pointer",
                  border:
                    selectedFloorThemeId === theme.id
                      ? "1px solid rgba(45, 150, 255, 0.6)"
                      : "1px solid rgba(0,0,0,0.08)",
                  background:
                    selectedFloorThemeId === theme.id
                      ? "rgba(45, 150, 255, 0.06)"
                      : "#fafbfc",
                }}
                onClick={() => setSelectedFloorThemeId(theme.id)}
              >
                <Stack gap={6}>
                  <Text size="xs" fw={600} c="dark" lineClamp={1}>
                    {theme.name}
                  </Text>
                  <Group gap={6}>
                    <Box
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: theme.floorTop,
                      }}
                    />
                    <Box
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: theme.floorSide,
                      }}
                    />
                    <Box
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: theme.porchMarker,
                      }}
                    />
                  </Group>
                </Stack>
              </Card>
            ))}
          </Box>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleCreateRoom()}
              loading={creating}
              disabled={
                !canCreateRoom ||
                !selectedLayout ||
                !selectedWallTheme ||
                !selectedFloorTheme
              }
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
